"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useRouter } from "next/navigation"
import { IUser, LoginRequest, RegisterRequest } from '@/lib/types'
import { auth } from '@/lib/api'
import { clientStorage } from '@/lib/client-utils'

// Типы уровней доступа
// admin = Администратор
// hr = HR
// project-manager = Руководитель проектного офиса
// department-head = РО
// implementation-head = РО реализации
// senior-engineer = Руководитель проекта
// engineer = Исполнитель
export type AccessLevel = "admin" | "hr" | "project-manager" | "department-head" | "implementation-head" | "senior-engineer" | "engineer"

// Тип авторизованного пользователя
export type AuthUser = {
  id: string
  name: string
  accessLevel: AccessLevel
}

interface AuthContextType {
  user: IUser | null
  loading: boolean
  error: string | null
  login: (data: LoginRequest) => Promise<void>
  register: (data: RegisterRequest) => Promise<void>
  logout: () => void
  hasPermission: (requiredLevels: string[]) => boolean
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const levelMap: { [key: string]: number } = {
  'admin': 7,
  'hr': 6,
  'project-manager': 5,
  'department-head': 4,
  'implementation-head': 3,
  'senior-engineer': 2,
  'engineer': 1,
}

// Хук для доступа к контексту аутентификации
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth должен использоваться внутри AuthProvider")
  }
  return context
}

// Провайдер аутентификации
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<IUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const router = useRouter()

  useEffect(() => {
    const initializeAuth = async () => {
      const token = clientStorage.getItem('token')
      if (!token) {
        console.log('No token found')
        setLoading(false)
        return
      }

      try {
        // Verify token expiration
        const tokenData = JSON.parse(atob(token.split('.')[1]))
        if (tokenData.exp * 1000 < Date.now()) {
          console.log('Token expired')
          clientStorage.removeItem('token')
          setLoading(false)
          return
        }

        console.log('Token valid, fetching user data')
        // Fetch current user
        const response = await auth.getCurrentUser()
        console.log('User data received:', response)
        
        if (!response.user) {
          console.error('No user data in response')
          clientStorage.removeItem('token')
          setLoading(false)
          return
        }

        setUser(response.user)
        if (response.token) {
          console.log('Updating token')
          clientStorage.setItem('token', response.token)
        }
        // Сохраняем данные пользователя в localStorage для использования в API
        clientStorage.setItem('user', JSON.stringify(response.user))
      } catch (err) {
        console.error('Failed to initialize auth:', err)
        clientStorage.removeItem('token')
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    initializeAuth()
  }, [])

  const login = async (data: LoginRequest) => {
    try {
      setError(null)
      const response = await auth.login(data)
      clientStorage.setItem('token', response.token)
      setUser(response.user)
      // Сохраняем данные пользователя в localStorage для использования в API
      clientStorage.setItem('user', JSON.stringify(response.user))
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка при входе в систему'
      setError(errorMessage)
      throw errorMessage
    }
  }

  const register = async (data: RegisterRequest) => {
    try {
      setError(null)
      const response = await auth.register(data)
      clientStorage.setItem('token', response.token)
      setUser(response.user)
      // Сохраняем данные пользователя в localStorage для использования в API
      clientStorage.setItem('user', JSON.stringify(response.user))
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка при регистрации'
      setError(errorMessage)
      throw errorMessage
    }
  }

  const logout = () => {
    setLoading(true)
    
    auth.logout()
    setUser(null)
    setError(null)
    
    // Очищаем данные пользователя из localStorage
    clientStorage.removeItem('user')
    
    setLoading(false)
    
    router.push("/login")
  }

  const hasPermission = (requiredLevels: string[]) => {
    if (!user) return false
    const userLevel = levelMap[user.accessLevel] || 0
    return requiredLevels.some(level => userLevel >= (levelMap[level] || 0))
  }

  const refreshUser = async () => {
    try {
      console.log('Refreshing user data...')
      const response = await auth.getCurrentUser()
      console.log('Refreshed user data:', response)
      
      if (response.user) {
        setUser(response.user)
        if (response.token) {
          clientStorage.setItem('token', response.token)
        }
        // Сохраняем данные пользователя в localStorage для использования в API
        clientStorage.setItem('user', JSON.stringify(response.user))
      }
    } catch (err) {
      console.error('Failed to refresh user:', err)
      // В случае ошибки не очищаем пользователя, так как это может быть временная проблема
    }
  }

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      login,
      register,
      logout,
      hasPermission,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  )
}

// Функция для проверки прав доступа
export function hasPermission(requiredLevel: AccessLevel, userLevel: AccessLevel): boolean {
  const levels: AccessLevel[] = ["engineer", "senior-engineer", "implementation-head", "department-head", "project-manager", "hr", "admin"]
  const requiredLevelIndex = levels.indexOf(requiredLevel)
  const userLevelIndex = levels.indexOf(userLevel)

  return userLevelIndex >= requiredLevelIndex
}

